<template>
  <div class="create-logistics-job">
    <div class="modal create-logistics-job-modal">
      <div class="modal-background" @click="closeModal"></div>
      <button class="modal-close is-large" @click="closeModal"></button>

      <div class="modal-card">
        <section class="modal-card-body no-scrollbar has-text-left">
          <p class="subtitle is-4 has-text-success" style="margin-bottom:8px">
            {{ $t("logistics.create-new-job") }}
            <i
              class="fa fa-redo tag is-rounded is-warning"
              style="position:absolute; right:20px; padding:7px;"
              @click="resetForm(true)"
            />
          </p>

          <!-- Driver -->
          <div class="field has-addons order">
            <div class="control">
              <a class="button is-outlined slim">
                <i class="fa fa-truck" />
              </a>
            </div>
            <div class="control is-expanded">
              <input
                class="input is-info"
                type="text"
                :placeholder="driverPlaceholder"
                :value="driverName"
                @focus="clearDriver"
                @blur="blurDriverList"
                @keyup="lookupDrivers($event)"
                autocomplete="new-password"
              />
            </div>
          </div>
          <div v-if="drivers.length > 0" class="list">
            <span
              v-for="d in drivers"
              class="list-item no-scrollbar has-text-dark"
              :key="d.key"
              @click="selectDriver(d)"
            >{{ d.key }}</span>
          </div>
          <div v-else-if="this.driverName && !this.driverID" class="list">
            <span
              class="list-item no-scrollbar has-text-link"
              @click="clearDriver"
            >-- No matching driver --</span>
          </div>

          <!-- Customer -->
          <div class="field has-addons order">
            <div class="control">
              <a class="button is-outlined slim">
                <i class="fa fa-user" />
              </a>
            </div>
            <div class="control is-expanded">
              <input
                class="input is-info"
                v-model="phoneNumber"
                type="number"
                :placeholder="customerPlaceholder"
                autocomplete="new-password"
              />
            </div>
          </div>

          <!-- offer -->
          <div class="field has-addons order">
            <div class="control">
              <a class="button is-outlined slim">
                <!-- <i class="fa fa-dollar-sign" /> -->
                <span class="icon has-text-weight-bold">{{ currency }}</span>
                <!-- &nbsp;{{ $t("delivery.delivery-fee") }} -->
                <!-- {{ $t("price") }} -->
              </a>
            </div>
            <div class="control is-expanded">
              <input
                class="input is-info"
                v-model="offer"
                type="number"
                :placeholder="offerPlaceholder"
              />
            </div>
          </div>

          <div class="has-text-left has-text-weight-medium">
            <label class="has-text-link">{{ $t("logistics.job-details") }}</label>
          </div>

          <!-- pickup -->
          <div class="field has-addons order">
            <div class="control">
              <a class="button is-outlined field-name">
                <i class="fa fa-map-marker-alt has-text-link" />
                &nbsp;{{ $t("logistics.pickup") }}
              </a>
            </div>
            <div class="control is-expanded" @click="openSetAddressModal('pickup')">
              <input
                class="input is-info"
                v-model="pickupName"
                type="text"
                :placeholder="locationPlaceholder"
              />
            </div>
            <!-- <div class="control" @click="lookupAddress('pickup')"> -->
          </div>

          <!-- dropoff -->
          <div class="field has-addons order">
            <div class="control">
              <a class="button is-outlined field-name">
                <i class="fa fa-map-marker-alt has-text-success" />
                &nbsp;{{ $t("logistics.dropoff") }}
              </a>
            </div>
            <div class="control is-expanded" @click="openSetAddressModal('dropoff')">
              <input
                class="input is-info"
                v-model="dropoffName"
                type="text"
                :placeholder="locationPlaceholder"
              />
            </div>
            <!-- <div class="control" @click="lookupAddress('dropoff')"> -->
          </div>

          <!-- time -->
          <div class="field has-addons order">
            <div class="control">
              <a class="button is-outlined field-name">
                <i class="fa fa-clock has-text-primary" />
                &nbsp;{{ $t("datetime") }}
              </a>
            </div>
            <div class="control" style="width:calc(50vw - 74px);">
              <a
                v-if="date"
                class="button is-info is-outlined is-fullwidth"
                @click="date = null"
              >{{ shortDate }}</a>
              <b-field v-if="!date" type="is-info" style="margin-bottom:0px;">
                <b-datepicker v-model="date" placeholder="Date" :min-date="today"></b-datepicker>
              </b-field>
            </div>
            <div class="control" style="width:calc(50vw - 74px);">
              <a
                v-if="time"
                class="button is-info is-outlined is-fullwidth"
                @click="time = null"
              >{{ shortTime }}</a>
              <b-field v-if="!time" type="is-info" style="margin-bottom:0px">
                <b-timepicker v-model="time" placeholder="Time" hour-format="24"></b-timepicker>
              </b-field>
            </div>
          </div>

          <div class="has-text-left has-text-weight-medium" @click="showGoods = !showGoods">
            <label class="has-text-link">{{ $t("logistics.goods-info") }}</label>
            <i class="far fa-window-minimize is-pulled-right has-text-link" />
          </div>

          <div v-if="showGoods">
            <!-- Goods description -->
            <div class="field has-addons order">
              <div class="control">
                <a class="button is-outlined field-name">
                  <i class="fa fa-box" />
                  &nbsp;{{ $t("logistics.goods") }}
                </a>
              </div>
              <div class="control is-expanded">
                <input
                  class="input is-info"
                  v-model="goods"
                  type="text"
                  :placeholder="goodsPlaceholder"
                />
              </div>
            </div>

            <!-- Goods Value -->
            <div class="field has-addons order">
              <div class="control">
                <a class="button is-outlined field-name">
                  <i class="fa fa-dollar-sign" />
                  &nbsp;{{ $t("logistics.value") }}
                </a>
              </div>
              <div class="control is-expanded">
                <input class="input is-info" v-model="orderValue" type="number" placeholder="100" />
              </div>
            </div>

            <!-- Job Notes -->
            <div class="field">
              <div class="control">
                <textarea
                  class="textarea is-primary"
                  v-model="notes"
                  :placeholder="notesPlaceholder"
                  rows="2"
                ></textarea>
              </div>
            </div>
          </div>

          <div v-else style="margin-bottom:12px" @click="showGoods = true">
            <a class="button is-outlined is-fullwidth">
              <i class="fa fa-ellipsis-h" />
            </a>
          </div>

          <button
            class="button is-success is-fullwidth is-rounded"
            v-clipboard:copy="shortLinkMessage()"
            @click="preSubmit"
            :disabled="!this.phoneNumber || !this.driverID || !this.offer"
          >{{ $t("logistics.create-job") }}</button>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import EventBus from "../eventBus.js";
import geo from "@/geo";

const shortid = require("shortid");

export default {
  name: "CreateLogisticsJobModal",
  components: {},
  data() {
    return {
      phoneNumber: null,
      pickupName: null,
      dropoffName: null,
      today: new Date(),
      date: null,
      time: null,
      offer: null,
      orderValue: null,
      goods: null,
      notes: null,
      pickup: null,
      dropoff: null,
      drivers: [],
      driverName: "",
      selectedDriver: "",
      pickupCallback: "LOGISTICS_JOB_PICKUP_CALLBACK",
      dropoffCallback: "LOGISTICS_JOB_DROPOFF_CALLBACK",
      showGoods: true,
      z: 0,
      // link: shortid.generate() // short code
    };
  },
  computed: {
    role() {
      return this.$store.getters.getLogisticsAgentRole;
    },
    workflow() {
      return this.role.workflow;
    },
    currency() {
      return this.currencySymbol(this.role.business.country);
    },
    driverPlaceholder() {
      return this.$t("logistics.driver-name-or-mobile");
    },
    customerPlaceholder() {
      return this.$t("logistics.customer-mobile");
    },
    offerPlaceholder() {
      return this.$t("logistics.job-amount");
    },
    goodsPlaceholder() {
      return this.$t("logistics.goods-description");
    },
    notesPlaceholder() {
      return "📝 " + this.$t("logistics.job-notes");
    },
    locationPlaceholder() {
      return this.$t("logistics.location");
    },
    shortDate() {
      return this.date.toLocaleString("en-GB").slice(0, 5);
    },
    shortTime() {
      return this.time.toLocaleTimeString("en-GB").slice(0, 5);
    },
    vehicles() {
      return this.$store.getters.getLogisticsFleet;
    },
    kvDrivers() {
      var arr = this.vehicles.map(function (v) {
        return {
          key: `${v.user.name} (${v.user.phone_number}) ${v.role.workflow_name}`,
          value: v.user.id,
        };
      });
      return arr;
    },
  },
  mounted() {
    EventBus.$on(this.pickupCallback, (data) => {
      console.log(this.pickupCallback, data);
      this.pickup = data;
      this.pickupName = data.name || "Pin location";
    });
    EventBus.$on(this.dropoffCallback, (data) => {
      console.log(this.dropoffCallback, data);
      this.dropoff = data;
      this.dropoffName = data.name || "Pin location";
    });
  },
  methods: {
    closeModal() {
      this.$el.querySelector(".modal").classList.remove("is-active");
    },
    resetForm(showToast) {
      Object.assign(this.$data, this.$options.data());
      if (showToast) this.warningToast("Reset form 👍🏼", "top");
    },
    openSetAddressModal(type) {
      var callback = this.pickupCallback;
      if (type === "dropoff") callback = this.dropoffCallback;
      EventBus.$emit("SET_ADDRESS_MODAL", callback);
      document.activeElement.blur();
    },
    selectDriver(d) {
      this.driverID = d.value;
      console.log("selectDriver", d);
      this.driverName = d.key;
      this.drivers = [];
    },
    blurDriverList() {
      setTimeout(() => (this.drivers = []), 200);
    },
    clearDriver() {
      this.driverName = "";
      this.driverID = "";
      this.drivers = this.kvDrivers;
    },
    lookupDrivers(e) {
      var text = e.target.value;
      this.driverName = text;
      var lower = this.trim0(text).toLowerCase();
      this.drivers = this.kvDrivers.filter((k) =>
        k.key.toLowerCase().includes(lower)
      );
    },
    trim0(s) {
      if (s[0] === "0") return s.substring(1); // remove leading 0
      return s;
    },
    shortLinkMessage() {},
    parseScheduledTime() {
      if (!this.date) return new Date().toISOString(); // default to NOW
      var dt = this.date;
      dt.setHours(this.time.getHours());
      dt.setMinutes(this.time.getMinutes());
      // if (dt < new Date()) return null; // can't be in the past
      if (dt < new Date()) dt = new Date();
      return dt.toISOString();
    },
    parsePhoneNumber() {
      if (this.phoneNumber.length < 8) return null;
      if (this.phoneNumber[0] === "0") {
        return "+66" + this.phoneNumber.substring(1);
      }
    },
    tripDistance() {
      var dist = geo.distanceToUserInMeters(this.pickup, this.dropoff);
      var drivingDist = Math.round(dist * 1.25);
      return drivingDist;
    },
    getFare() {
      return {
        version: "v1",
        price: this.offer,
        discount: 0,
        cash: this.offer,
        tukpay: 0,
        distance: this.tripDistance(),
      };
    },
    preSubmit() {
      // check necessary fields completed, validate phone number
      if (!this.parsePhoneNumber()) return this.errorToast("Invalid mobile");
      if (this.offer < 0) return this.errorToast("Enter a valid price");
      if (!this.pickup) return this.errorToast("Set pickup location");
      if (!this.dropoff) return this.errorToast("Set dropoff location");
      if (this.date && !this.time) return this.errorToast("Set a time");
      if (this.time && !this.date) this.date = new Date(); // return this.errorToast("Set a date");
      var datetime = this.parseScheduledTime();
      // if (!datetime) return this.errorToast("Time cannot be in the past");
      var green = this.bulmaColor("primary");
      var shortTime = this.shortDateTime(datetime);
      var goods = this.goods || this.$t("none");
      var value = this.orderValue || this.$t("none");
      var notes = this.notes || this.$t("none");
      var txt1 = this.$t("logistics.confirm-details");
      var txt2 = this.$t("logistics.pickup-time");
      var txt3 = this.$t("logistics.goods");
      var txt4 = this.$t("logistics.value");
      var txt5 = this.$t("notes");
      this.$swal({
        title: `<div style='color:${green}'><i class='fa fa-truck'></i>&nbsp;<i class='fa fa-user'></i></div>`,
        html: `<div style='text-align:left'><i>${txt1}</i><br>
        ${txt2}: <strong>${shortTime}</strong><br>
        ${txt3}: <strong>${goods}</strong><br>
        ${txt4}: <strong>${this.currency} ${value}</strong><br>
        ${txt5}: <strong>${notes}</strong>
        </div>`,
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: this.$t("create"),
        confirmButtonColor: green,
        cancelButtonText: this.$t("cancel"),
      }).then((val) => {
        if (val.value) this.submit();
      });
    },
    // create transaction
    submit() {
      var delivery = {
        type: "Logistics",
        business_name: this.role.business.name, // this logistics business
        workflow_id: this.role.workflow_id, // this logistics workflow
        agent_id: this.userID(),
        order_value: this.orderValue, // optional
        goods: this.goods,
        notes: this.notes,
        short_link: shortid.generate(),
        // payment_method: "cash" // optional
      };
      var obj = {
        type: "scheduled",
        pickup_stop: this.pickup,
        dropoff_stop: this.dropoff,
        fare: this.getFare(),
        scheduled_time: this.parseScheduledTime(),
        delivery: delivery,
      };
      var loader = this.loadingDots();
      this.$store
        .dispatch("createTransaction", {
          workflow_type_name: "Logistics", //"Delivery"
          workflow_id: "", // leave blank will get set by driver ID
          business_id: this.role.business_id,
          created_by: this.parsePhoneNumber(), // customer phone number
          handled_by: this.driverID,
          data: obj,
        })
        .then(() => {
          this.successToast(this.$t("transport.waiting-for-confirmation"));
          this.updateOrdersLogisticsAgent();
          this.resetForm();
          this.closeModal();
        })
        .catch((err) => this.apiError(err))
        .finally(() => loader.hide());
    },
  },
};
</script>

<style scoped>
.modal-card-body {
  padding: 10px;
  border-radius: 25px;
  max-width: 640px;
}
.field {
  margin-bottom: 10px;
}
.slim {
  width: 50px;
}
.field-name {
  text-align: left !important;
  width: 110px;
}
.list-item {
  width: 100%;
  white-space: nowrap;
  overflow: auto;
  display: inline-block;
  text-align: left;
}
.list-item:not(:first-child) {
  padding-top: 4px;
}
.list-item:last-child {
  padding-bottom: 0px;
}
.list {
  margin-top: -10px;
  background-color: snow;
  margin-bottom: 20px;
  position: absolute;
  z-index: 5;
  width: calc(100vw - 40px);
  max-width: 620px;
  /* box-shadow: 1px 2px 2px 2px rgba(62, 113, 255, 0.8); */
  box-shadow: 1px 2px 2px 2px rgba(69, 76, 96, 0.8);
}
/* .has-icons-right {
  padding-right: 0 !important;
  margin-right: 0;
}
input.input .control.has-icons-right .select {
  padding-right: 0 !important;
} */
</style>